import React, { useState, useEffect, useCallback } from 'react';
import { smtpAPI, envAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import {
  Mail,
  Server,
  Lock,
  Send,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Database,
  ShieldAlert,
  Clock,
  History,
  KeyRound,
  Check,
} from 'lucide-react';

const SystemSettings = () => {
  const { user, isAdmin } = useAuthStore();
  const [activeTab, setActiveTab] = useState('smtp'); // 'smtp' | 'env' | 'queue' | 'audit'

  // Alerts
  const [alert, setAlert] = useState(null); // { type: 'success'|'error'|'info', text: string }

  // ─── SMTP State ────────────────────────────────────────────────────────────
  const [smtpForm, setSmtpForm] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
    smtp_encryption: 'TLS',
    has_password: false,
    is_configured: false,
  });
  const [smtpLoading, setSmtpLoading] = useState(true);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  // ─── Environment & DB URL State ────────────────────────────────────────────
  const [envVars, setEnvVars] = useState([]);
  const [envLoading, setEnvLoading] = useState(false);
  const [editingEnvKey, setEditingEnvKey] = useState(null);
  const [envEditValue, setEnvEditValue] = useState('');
  const [dbTestResult, setDbTestResult] = useState(null);
  const [dbTesting, setDbTesting] = useState(false);
  const [showAdminAuthModal, setShowAdminAuthModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [authActionPayload, setAuthActionPayload] = useState(null);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  // ─── Outbox Queue State ────────────────────────────────────────────────────
  const [queueItems, setQueueItems] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [drainingQueue, setDrainingQueue] = useState(false);

  // ─── Audit Log State ───────────────────────────────────────────────────────
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Helper alert setter
  const notify = (type, text) => {
    setAlert({ type, text });
    setTimeout(() => setAlert(null), 7000);
  };

  // ─── Fetchers ──────────────────────────────────────────────────────────────
  const loadSmtp = useCallback(async () => {
    setSmtpLoading(true);
    try {
      const res = await smtpAPI.get();
      if (res.data?.data) {
        const d = res.data.data;
        setSmtpForm({
          smtp_host: d.smtp_host || 'smtp.gmail.com',
          smtp_port: d.smtp_port || 587,
          smtp_user: d.smtp_user || '',
          smtp_password: d.has_password ? '••••••••••••••••' : '',
          smtp_from: d.smtp_from || '',
          smtp_encryption: d.smtp_encryption || 'TLS',
          has_password: !!d.has_password,
          is_configured: !!d.is_configured,
        });
        if (d.smtp_user && !testRecipient) {
          setTestRecipient(d.smtp_user);
        }
      }
    } catch (err) {
      console.error('Failed to load SMTP settings:', err);
    } finally {
      setSmtpLoading(false);
    }
  }, [testRecipient]);

  const loadEnv = useCallback(async () => {
    setEnvLoading(true);
    try {
      const res = await envAPI.list();
      if (res.data?.data) {
        setEnvVars(res.data.data);
      }
    } catch (err) {
      notify('error', 'Failed to load environment variables: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnvLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const res = await smtpAPI.getQueue(50);
      if (res.data?.data) {
        setQueueItems(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load outbox queue:', err);
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await envAPI.getAuditLogs(50);
      if (res.data?.data) {
        setAuditLogs(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSmtp();
  }, [loadSmtp]);

  useEffect(() => {
    if (activeTab === 'env') loadEnv();
    if (activeTab === 'queue') loadQueue();
    if (activeTab === 'audit') loadAudit();
  }, [activeTab, loadEnv, loadQueue, loadAudit]);

  // ─── SMTP Handlers ─────────────────────────────────────────────────────────
  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    try {
      const payload = {
        smtp_host: smtpForm.smtp_host,
        smtp_port: parseInt(smtpForm.smtp_port, 10),
        smtp_user: smtpForm.smtp_user,
        smtp_encryption: smtpForm.smtp_encryption,
        smtp_from: smtpForm.smtp_from,
      };
      if (smtpForm.smtp_password && smtpForm.smtp_password !== '••••••••••••••••') {
        payload.smtp_password = smtpForm.smtp_password;
      }
      const res = await smtpAPI.update(payload);
      notify('success', res.data?.message || 'SMTP Configuration saved successfully!');
      loadSmtp();
    } catch (err) {
      notify('error', err.response?.data?.detail || 'Failed to save SMTP configuration.');
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testRecipient) {
      notify('error', 'Please enter a recipient email address for testing.');
      return;
    }
    setTestLoading(true);
    try {
      const res = await smtpAPI.test({ recipient: testRecipient });
      notify('success', res.data?.message || 'Test email sent successfully! Please check your inbox.');
    } catch (err) {
      notify('error', err.response?.data?.detail || 'SMTP test failed. Check host, port, credentials, and App Password.');
    } finally {
      setTestLoading(false);
    }
  };

  // ─── Environment & DB URL Handlers ─────────────────────────────────────────
  const handleTestDb = async (urlToTest) => {
    setDbTesting(true);
    setDbTestResult(null);
    try {
      const res = await envAPI.testDbConnection({ database_url: urlToTest });
      setDbTestResult({
        success: true,
        message: res.data?.message || 'Database connection successful!',
      });
    } catch (err) {
      setDbTestResult({
        success: false,
        message: err.response?.data?.detail || 'Database connection failed. Please verify credentials, host, and port.',
      });
    } finally {
      setDbTesting(false);
    }
  };

  const startEditEnv = (item) => {
    setEditingEnvKey(item.key);
    setEnvEditValue(item.is_masked ? '' : item.value || '');
  };

  const cancelEditEnv = () => {
    setEditingEnvKey(null);
    setEnvEditValue('');
    setDbTestResult(null);
  };

  const triggerAuthSave = (key, value) => {
    setAuthActionPayload({ key, value });
    setAdminPassword('');
    setShowAdminAuthModal(true);
  };

  const executeAuthenticatedEnvSave = async (e) => {
    e.preventDefault();
    if (!adminPassword) {
      notify('error', 'Admin password is required to authorize configuration changes.');
      return;
    }
    setAuthSubmitting(true);
    try {
      const { key, value } = authActionPayload;
      const res = await envAPI.update({
        admin_password: adminPassword,
        variables: [{ key, value }],
      });
      notify('success', res.data?.message || `Successfully updated ${key}.`);
      setShowAdminAuthModal(false);
      setEditingEnvKey(null);
      setEnvEditValue('');
      loadEnv();
    } catch (err) {
      notify('error', err.response?.data?.detail || 'Failed to update variable. Check admin password.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  // ─── Queue Handlers ────────────────────────────────────────────────────────
  const handleDrainQueue = async () => {
    setDrainingQueue(true);
    try {
      const res = await smtpAPI.retryPending();
      const r = res.data?.data;
      notify('success', `Queue processing finished: ${r?.sent || 0} sent, ${r?.failed || 0} failed.`);
      loadQueue();
    } catch (err) {
      notify('error', err.response?.data?.detail || 'Failed to process pending email queue.');
    } finally {
      setDrainingQueue(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <ShieldAlert size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
        <h2>Access Restricted</h2>
        <p style={{ color: '#64748b' }}>Only system administrators can access System Settings.</p>
      </div>
    );
  }

  const dbVar = envVars.find((v) => v.key === 'DATABASE_URL');

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px 80px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 700, color: 'var(--text-primary)' }}>
            System Settings
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
            Manage outbound SMTP email delivery, database connection strings, and system environment variables.
          </p>
        </div>

        {/* Global Badges */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: smtpForm.is_configured ? '#ecfdf5' : '#fffbeb',
              color: smtpForm.is_configured ? '#065f46' : '#92400e',
              border: `1px solid ${smtpForm.is_configured ? '#10b981' : '#f59e0b'}`,
            }}
          >
            <Mail size={14} />
            {smtpForm.is_configured ? 'SMTP Active' : 'SMTP Defaults'}
          </div>
        </div>
      </div>

      {/* Global Alert */}
      {alert && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            backgroundColor: alert.type === 'success' ? '#ecfdf5' : alert.type === 'error' ? '#fef2f2' : '#eff6ff',
            color: alert.type === 'success' ? '#065f46' : alert.type === 'error' ? '#991b1b' : '#1e40af',
            border: `1px solid ${alert.type === 'success' ? '#10b981' : alert.type === 'error' ? '#ef4444' : '#3b82f6'}`,
          }}
        >
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.text}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          borderBottom: '2px solid #e2e8f0',
          marginBottom: '24px',
          overflowX: 'auto',
        }}
      >
        <button
          onClick={() => setActiveTab('smtp')}
          style={{
            padding: '10px 18px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'smtp' ? '3px solid #0d9488' : '3px solid transparent',
            color: activeTab === 'smtp' ? '#0d9488' : '#64748b',
            fontWeight: activeTab === 'smtp' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '-2px',
          }}
        >
          <Mail size={16} /> SMTP Email Delivery
        </button>

        <button
          onClick={() => setActiveTab('env')}
          style={{
            padding: '10px 18px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'env' ? '3px solid #0d9488' : '3px solid transparent',
            color: activeTab === 'env' ? '#0d9488' : '#64748b',
            fontWeight: activeTab === 'env' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '-2px',
          }}
        >
          <Server size={16} /> Environment & Database URL
        </button>

        <button
          onClick={() => setActiveTab('queue')}
          style={{
            padding: '10px 18px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'queue' ? '3px solid #0d9488' : '3px solid transparent',
            color: activeTab === 'queue' ? '#0d9488' : '#64748b',
            fontWeight: activeTab === 'queue' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '-2px',
          }}
        >
          <Send size={16} /> Outbox Queue
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          style={{
            padding: '10px 18px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'audit' ? '3px solid #0d9488' : '3px solid transparent',
            color: activeTab === 'audit' ? '#0d9488' : '#64748b',
            fontWeight: activeTab === 'audit' ? 700 : 500,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '-2px',
          }}
        >
          <History size={16} /> Security Audit Logs
        </button>
      </div>

      {/* ─── TAB 1: SMTP Settings ────────────────────────────────────────────── */}
      {activeTab === 'smtp' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '24px', alignItems: 'start' }}>
          {/* Main SMTP Form Card */}
          <div
            style={{
              background: 'var(--card-bg, #ffffff)',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Outbound SMTP Configuration</h2>
              {smtpLoading && <RefreshCw size={16} className="spinner" />}
            </div>

            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: 1.5 }}>
              Enter your SMTP credentials below. For Gmail, use an <strong>App Password</strong> generated via 2-Factor Authentication.
            </p>

            <form onSubmit={handleSaveSmtp}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>SMTP Host</label>
                  <input
                    type="text"
                    className="form-control"
                    value={smtpForm.smtp_host}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Port</label>
                  <input
                    type="number"
                    className="form-control"
                    value={smtpForm.smtp_port}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_port: e.target.value })}
                    placeholder="587"
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Encryption</label>
                  <select
                    className="form-control"
                    value={smtpForm.smtp_encryption}
                    onChange={(e) => {
                      const enc = e.target.value;
                      setSmtpForm({
                        ...smtpForm,
                        smtp_encryption: enc,
                        smtp_port: enc === 'SSL' ? 465 : 587,
                      });
                    }}
                  >
                    <option value="TLS">STARTTLS (Port 587)</option>
                    <option value="SSL">SSL / TLS (Port 465)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>Sender Email / Username</label>
                  <input
                    type="email"
                    className="form-control"
                    value={smtpForm.smtp_user}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                    placeholder="timesheet@tickmarks.net"
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>
                  SMTP / App Password (16 characters)
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showSmtpPassword ? 'text' : 'password'}
                    className="form-control"
                    style={{ paddingRight: '44px' }}
                    value={smtpForm.smtp_password}
                    onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                    placeholder="Enter 16-character Google App Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                      padding: '4px',
                    }}
                  >
                    {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                  {smtpForm.has_password
                    ? 'A password is encrypted and saved. Leave as •••••••• to keep it, or enter a new App Password to update.'
                    : 'Paste the 16-character password generated via Google Account Security > 2-Step Verification > App passwords.'}
                </span>
              </div>

              <div style={{ marginBottom: '22px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>From Header (Display Name)</label>
                <input
                  type="text"
                  className="form-control"
                  value={smtpForm.smtp_from}
                  onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from: e.target.value })}
                  placeholder="Tickmarks TimeTracker <timesheet@tickmarks.net>"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', padding: '12px', fontWeight: 600 }}
                disabled={smtpSaving || smtpLoading}
              >
                {smtpSaving ? 'Saving Configuration...' : 'Save SMTP Configuration'}
              </button>
            </form>
          </div>

          {/* Test & Instructions Card */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Live Test Box */}
            <div
              style={{
                background: 'var(--card-bg, #ffffff)',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Send size={18} color="#0d9488" /> Test Email Delivery
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                Verify your SMTP credentials by sending a live test message.
              </p>

              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="email"
                  className="form-control"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="Recipient email address"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleTestSmtp}
                  disabled={testLoading || !smtpForm.smtp_user}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {testLoading ? 'Testing...' : 'Send Test'}
                </button>
              </div>
            </div>

            {/* Google App Password Guide */}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '20px',
                fontSize: '13px',
                color: '#475569',
              }}
            >
              <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <KeyRound size={16} color="#0d9488" /> How to generate Google App Password
              </div>
              <ol style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.6 }}>
                <li>Log in to your Google Account at <strong>myaccount.google.com</strong>.</li>
                <li>Go to <strong>Security</strong> & ensure <strong>2-Step Verification</strong> is ON.</li>
                <li>Search for <strong>"App passwords"</strong> in the search bar.</li>
                <li>Enter an app name like <code>Timesheet Tracker</code> and click <strong>Create</strong>.</li>
                <li>Copy the 16-character code (spaces will be stripped automatically) and paste it above.</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: Environment Variables & Database URL ─────────────────────── */}
      {activeTab === 'env' && (
        <div>
          {/* Prominent Database URL Card */}
          <div
            style={{
              background: '#f8fafc',
              border: '2px solid #0d9488',
              borderRadius: '12px',
              padding: '20px 24px',
              marginBottom: '24px',
              boxShadow: '0 2px 4px rgba(13,148,136,0.08)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Database size={24} color="#0d9488" />
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                    PostgreSQL Database URL
                  </h3>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Accommodates local instances (e.g. <code>postgresql://user:pass@localhost:5432/dbname</code>) or remote PostgreSQL servers.
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                {editingEnvKey === 'DATABASE_URL' ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleTestDb(envEditValue)}
                      disabled={dbTesting || !envEditValue}
                      style={{ fontSize: '13px' }}
                    >
                      {dbTesting ? 'Testing Connection...' : 'Test Connection'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => triggerAuthSave('DATABASE_URL', envEditValue)}
                      style={{ fontSize: '13px' }}
                    >
                      Save with Admin Auth
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={cancelEditEnv}
                      style={{ fontSize: '13px' }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleTestDb(dbVar?.value || '')}
                      disabled={dbTesting}
                      style={{ fontSize: '13px' }}
                    >
                      {dbTesting ? 'Testing Connection...' : 'Test Current Connection'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => startEditEnv(dbVar || { key: 'DATABASE_URL', value: '' })}
                      style={{ fontSize: '13px' }}
                    >
                      Edit Connection String
                    </button>
                  </>
                )}
              </div>
            </div>

            {editingEnvKey === 'DATABASE_URL' ? (
              <div style={{ marginTop: '14px' }}>
                <textarea
                  className="form-control"
                  rows={2}
                  value={envEditValue}
                  onChange={(e) => setEnvEditValue(e.target.value)}
                  placeholder="postgresql://username:password@hostname:5432/database_name"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            ) : (
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: '#334155',
                  wordBreak: 'break-all',
                }}
              >
                {dbVar?.value || 'postgresql://timesheet:••••••••@localhost:5432/timesheet'}
              </div>
            )}

            {/* Test result feedback */}
            {dbTestResult && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  background: dbTestResult.success ? '#ecfdf5' : '#fef2f2',
                  color: dbTestResult.success ? '#065f46' : '#991b1b',
                  border: `1px solid ${dbTestResult.success ? '#10b981' : '#ef4444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {dbTestResult.success ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                <span>{dbTestResult.message}</span>
              </div>
            )}
          </div>

          {/* Full Environment Variables Table */}
          <div
            style={{
              background: 'var(--card-bg, #ffffff)',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Active Environment Variables</h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  All modifications require the administrator login password and are recorded in the security audit log.
                </span>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadEnv}
                disabled={envLoading}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                <RefreshCw size={13} className={envLoading ? 'spinner' : ''} /> Refresh
              </button>
            </div>

            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>
                  <th style={{ padding: '12px 18px', width: '25%' }}>Variable Key</th>
                  <th style={{ padding: '12px 18px', width: '45%' }}>Current Value</th>
                  <th style={{ padding: '12px 18px', width: '15%' }}>Source</th>
                  <th style={{ padding: '12px 18px', width: '15%', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {envVars.map((v) => (
                  <tr key={v.key} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <td style={{ padding: '12px 18px', fontWeight: 600, color: '#0f172a' }}>
                      <code>{v.key}</code>
                      {v.description && (
                        <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: 400, marginTop: '2px' }}>
                          {v.description}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 18px' }}>
                      {editingEnvKey === v.key ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            className="form-control"
                            value={envEditValue}
                            onChange={(e) => setEnvEditValue(e.target.value)}
                            placeholder="Enter new value"
                            style={{ fontSize: '12px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => triggerAuthSave(v.key, envEditValue)}
                            style={{ fontSize: '11px', padding: '4px 10px' }}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={cancelEditEnv}
                            style={{ fontSize: '11px', padding: '4px 10px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontFamily: 'monospace', color: v.is_masked ? '#94a3b8' : '#334155' }}>
                          {v.value || '(empty)'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 18px' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          background: v.source === 'database' ? '#f0fdf4' : '#f8fafc',
                          color: v.source === 'database' ? '#166534' : '#475569',
                          border: `1px solid ${v.source === 'database' ? '#bbf7d0' : '#e2e8f0'}`,
                        }}
                      >
                        {v.source === 'database' ? 'Database Override' : 'Environment (.env)'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 18px', textAlign: 'right' }}>
                      {editingEnvKey !== v.key && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => startEditEnv(v)}
                          style={{ fontSize: '11px', padding: '4px 10px' }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB 3: Outbox Queue ──────────────────────────────────────────────── */}
      {activeTab === 'queue' && (
        <div
          style={{
            background: 'var(--card-bg, #ffffff)',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Outbound Email Queue</h3>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                Monitors all system-generated emails, verification links, OTP codes, and approval notices.
              </span>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDrainQueue}
                disabled={drainingQueue}
                style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Send size={14} className={drainingQueue ? 'spinner' : ''} />
                {drainingQueue ? 'Processing Queue...' : 'Process & Send Pending Queue Now'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={loadQueue}
                disabled={queueLoading}
                style={{ fontSize: '13px' }}
              >
                <RefreshCw size={13} className={queueLoading ? 'spinner' : ''} /> Refresh
              </button>
            </div>
          </div>

          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>
                <th style={{ padding: '12px 18px', width: '8%' }}>ID</th>
                <th style={{ padding: '12px 18px', width: '22%' }}>Recipient</th>
                <th style={{ padding: '12px 18px', width: '30%' }}>Subject</th>
                <th style={{ padding: '12px 18px', width: '15%' }}>Status</th>
                <th style={{ padding: '12px 18px', width: '25%' }}>Created / Sent At</th>
              </tr>
            </thead>
            <tbody>
              {queueItems.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    {queueLoading ? 'Loading queue items...' : 'Outbox queue is empty. No pending emails.'}
                  </td>
                </tr>
              ) : (
                queueItems.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <td style={{ padding: '12px 18px', color: '#64748b' }}>#{item.id}</td>
                    <td style={{ padding: '12px 18px', fontWeight: 500, color: '#0f172a' }}>{item.recipient}</td>
                    <td style={{ padding: '12px 18px', color: '#334155' }}>{item.subject}</td>
                    <td style={{ padding: '12px 18px' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: item.status === 'SENT' ? '#ecfdf5' : item.status === 'FAILED' ? '#fef2f2' : '#fffbeb',
                          color: item.status === 'SENT' ? '#065f46' : item.status === 'FAILED' ? '#991b1b' : '#92400e',
                          border: `1px solid ${item.status === 'SENT' ? '#10b981' : item.status === 'FAILED' ? '#ef4444' : '#f59e0b'}`,
                        }}
                      >
                        {item.status}
                        {item.is_sent && <Check size={11} />}
                      </span>
                      {item.error_message && (
                        <span style={{ display: 'block', fontSize: '11px', color: '#ef4444', marginTop: '2px', wordBreak: 'break-all' }}>
                          {item.error_message}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 18px', fontSize: '12px', color: '#64748b' }}>
                      <div>Created: {item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A'}</div>
                      {item.sent_at && <div>Sent: {new Date(item.sent_at).toLocaleString()}</div>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── TAB 4: Security Audit Logs ───────────────────────────────────────── */}
      {activeTab === 'audit' && (
        <div
          style={{
            background: 'var(--card-bg, #ffffff)',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>System Configuration Audit Trail</h3>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                Permanent audit log of all environment variable and system credential modifications.
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={loadAudit}
              disabled={auditLoading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              <RefreshCw size={13} className={auditLoading ? 'spinner' : ''} /> Refresh
            </button>
          </div>

          <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', fontSize: '12px', color: '#64748b' }}>
                <th style={{ padding: '12px 18px', width: '15%' }}>Timestamp</th>
                <th style={{ padding: '12px 18px', width: '25%' }}>Admin User</th>
                <th style={{ padding: '12px 18px', width: '20%' }}>Action / Target</th>
                <th style={{ padding: '12px 18px', width: '25%' }}>Value Summary</th>
                <th style={{ padding: '12px 18px', width: '15%' }}>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    {auditLoading ? 'Loading audit records...' : 'No system modifications recorded yet.'}
                  </td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <td style={{ padding: '12px 18px', fontSize: '12px', color: '#64748b' }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}
                    </td>
                    <td style={{ padding: '12px 18px', fontWeight: 500, color: '#0f172a' }}>
                      {log.user_email}
                    </td>
                    <td style={{ padding: '12px 18px' }}>
                      <span style={{ fontWeight: 600, color: '#0d9488' }}>{log.action}</span>
                      <code style={{ display: 'block', fontSize: '11px', marginTop: '2px' }}>{log.target_key}</code>
                    </td>
                    <td style={{ padding: '12px 18px', fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>
                      <div>Old: {log.old_value_preview || '(none)'}</div>
                      <div>New: {log.new_value_preview || '(none)'}</div>
                    </td>
                    <td style={{ padding: '12px 18px', fontSize: '12px', color: '#64748b' }}>
                      {log.ip_address || 'Internal'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── Admin Password Re-Authentication Modal ───────────────────────── */}
      {showAdminAuthModal && (
        <div className="modal-overlay" onClick={() => !authSubmitting && setShowAdminAuthModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '440px', padding: '28px', borderRadius: '12px' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                }}
              >
                <Lock size={26} color="#ef4444" />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                Administrator Authorization Required
              </h3>
              <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>
                You are modifying <code>{authActionPayload?.key}</code>. Please confirm your administrator login password to authorize this update.
              </p>
            </div>

            <form onSubmit={executeAuthenticatedEnvSave}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block' }}>
                  Admin Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter your current login password"
                  autoFocus
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAdminAuthModal(false)}
                  disabled={authSubmitting}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={authSubmitting || !adminPassword}
                  style={{ flex: 1 }}
                >
                  {authSubmitting ? 'Verifying...' : 'Authorize Change'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;

